import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import {
  AccountClient,
  AccountDto,
  AccountOrderOptionEnum
} from '../../../core/services/service-clients';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { AccountsState } from './models/accounts.model';

const initialState: AccountsState = {
  accounts: [],
  totalRecords: 0,
  loading: false,
  lastParams: {
    search: null,
    skip: null,
    take: null,
    sort: null,
    orderByDescending: null,
  },
};

export const adminAccountsStore = signalStore(
  withState<AccountsState>(initialState),
  withMethods((store) => {
    const accountClient = inject(AccountClient);
    const messageService = inject(MessageService);

    return {
      async loadAccounts(
        search: string | null = null,
        skip: number = 0,
        take: number = 10,
        orderOption: AccountOrderOptionEnum = AccountOrderOptionEnum.Id,
        orderByDescending: boolean = false
      ): Promise<void> {
        patchState(store, { loading: true });

        try {
          // Load accounts and total count in parallel
          const [accounts, totalCount] = await Promise.all([
            firstValueFrom(
              accountClient.getUsers(search, skip, take, orderOption, orderByDescending)
            ),
            firstValueFrom(
              accountClient.getUsersCount(search)
            )
          ]);

          patchState(store, {
            accounts,
            totalRecords: totalCount,
            loading: false,
            lastParams: {
              search,
              skip,
              take,
              sort: AccountOrderOptionEnum[orderOption],
              orderByDescending,
            },
          });
        } catch (error) {
          console.error('Error loading accounts:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Benutzerkonten konnten nicht geladen werden'
          });
          patchState(store, { loading: false });
        }
      },

      async getUsersCount(search: string | null = null): Promise<number> {
        try {
          return await firstValueFrom(accountClient.getUsersCount(search));
        } catch (error) {
          console.error('Error loading users count:', error);
          return 0;
        }
      },

      async getAccountById(id: number): Promise<AccountDto | null> {
        try {
          return await firstValueFrom(accountClient.getUserById(id));
        } catch (error) {
          console.error('Error loading account:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Benutzerkonto konnte nicht geladen werden'
          });
          return null;
        }
      },

      async updateUserRole(id: number, roleId: number): Promise<boolean> {
        patchState(store, { loading: true });

        try {
          const updatedAccount = await firstValueFrom(
            accountClient.updateUserRole(id, roleId)
          );

          // Update the account in the current list
          const currentAccounts = store.accounts();
          const updatedAccounts = currentAccounts.map(account =>
            account.id === id ? updatedAccount : account
          );

          patchState(store, {
            accounts: updatedAccounts,
            loading: false
          });

          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Benutzerrolle wurde erfolgreich aktualisiert'
          });

          return true;
        } catch (error) {
          console.error('Error updating user role:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Benutzerrolle konnte nicht aktualisiert werden'
          });
          patchState(store, { loading: false });
          return false;
        }
      },

      async deleteUser(id: number): Promise<boolean> {
        patchState(store, { loading: true });

        try {
          await firstValueFrom(accountClient.deleteUser(id));

          // Remove the account from the current list and get updated total count
          const currentAccounts = store.accounts();
          const filteredAccounts = currentAccounts.filter(account => account.id !== id);
          const lastParams = store.lastParams();
          const updatedTotalCount = await firstValueFrom(accountClient.getUsersCount(lastParams.search));

          patchState(store, {
            accounts: filteredAccounts,
            totalRecords: updatedTotalCount,
            loading: false
          });

          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Benutzerkonto wurde erfolgreich gelöscht'
          });

          return true;
        } catch (error) {
          console.error('Error deleting user:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Benutzerkonto konnte nicht gelöscht werden'
          });
          patchState(store, { loading: false });
          return false;
        }
      },

      // Computed selectors
      selectAccountById: (id: number) => computed(() =>
        store.accounts().find(account => account.id === id) || null
      ),
    };
  })
);
